console.log('main.js is loading...')

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createAuth0 } from '@auth0/auth0-vue'
import { createRouter, createWebHistory } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

import App from './App.vue'
import './style.css'

// Import components
import Dashboard from './views/Dashboard.vue'
import Campaigns from './views/Campaigns.vue'
import Lists from './views/Lists.vue'
import Domains from './views/Domains.vue'
import Settings from './views/Settings.vue'
import CampaignDetail from './views/CampaignDetail.vue'
import Callback from './views/Callback.vue'
import { setAuth0Instance } from './services/auth'

// Create router
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'Dashboard',
      component: Dashboard,
      meta: { requiresAuth: true }
    },
    {
      path: '/campaigns',
      name: 'Campaigns',
      component: Campaigns,
      meta: { requiresAuth: true }
    },
    {
      path: '/campaigns/:id',
      name: 'CampaignDetail',
      component: CampaignDetail,
      meta: { requiresAuth: true }
    },
    {
      path: '/lists',
      name: 'Lists',
      component: Lists,
      meta: { requiresAuth: true }
    },
    {
      path: '/domains',
      name: 'Domains',
      component: Domains,
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      meta: { requiresAuth: true }
    },
    {
      path: '/callback',
      name: 'Callback',
      component: Callback,
      meta: { requiresAuth: false }
    }
  ]
})

// Get Auth0 config from runtime config or environment variables
const getAuth0Config = () => {
  const getConfig = (key) => {
    if (typeof window !== 'undefined' && window.APP_CONFIG?.[key]) {
      return window.APP_CONFIG[key]
    }
    return import.meta.env[key] || ''
  }
  
  return {
    domain: getConfig('VITE_AUTH0_DOMAIN'),
    clientId: getConfig('VITE_AUTH0_CLIENT_ID'),
    audience: getConfig('VITE_AUTH0_AUDIENCE')
  }
}

// Auth0 configuration
const auth0Config = getAuth0Config()
console.log('Auth0 configuration:', auth0Config)

const auth0 = createAuth0({
  domain: auth0Config.domain,
  clientId: auth0Config.clientId,
  authorizationParams: {
    redirect_uri: `${window.location.origin}/callback`,
    audience: auth0Config.audience,
    scope: 'openid profile email offline_access' // Explicitly include offline_access for refresh tokens
  },
  // Add additional configuration for better debugging
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
  // Let the plugin handle callbacks automatically
  skipRedirectCallback: false  // Change this from true to false
})

// Set the instance for the auth service
setAuth0Instance(auth0)

console.log('Auth0 instance created:', auth0)

// Create app
const app = createApp(App)

// Use plugins
app.use(createPinia())
app.use(router)
app.use(auth0)
app.use(toast, {
  autoClose: 3000,
  position: 'top-right',
  theme: 'colored'
})

// Debug Auth0 plugin initialization
console.log('Auth0 plugin installed, checking if it\'s trying to handle callbacks...')
if (window.location.search.includes('code=')) {
  console.log('Callback detected during app initialization, Auth0 plugin may try to handle it')
}

// TEMPORARILY DISABLE NAVIGATION GUARD TO DEBUG
// router.beforeEach(async (to, from, next) => {
//   console.log('Navigation guard triggered:', { to: to.path, from: from.path })
//   
//   // Get the Auth0 instance from the app
//   const auth0Instance = app.config.globalProperties.$auth0 || auth0
//   
//   console.log('Auth0 state:', { 
//     isLoading: auth0Instance.isLoading?.value, 
//     isAuthenticated: auth0Instance.isAuthenticated?.value 
//   })
//   
//   // Special handling for callback route - always allow it
//   if (to.path === '/callback') {
//     console.log('Allowing navigation to callback route')
//     next()
//     return
//   }
//   
//   // Wait for Auth0 to finish loading
//   if (auth0Instance.isLoading?.value) {
//     console.log('Auth0 still loading, waiting...')
//     // Wait for loading to complete with a timeout
//     let attempts = 0
//     const maxAttempts = 20 // 10 seconds max wait
//     
//     while (auth0Instance.isLoading?.value && attempts < maxAttempts) {
//       await new Promise(resolve => setTimeout(resolve, 500))
//       attempts++
//       console.log(`Waiting for Auth0 to finish loading... attempt ${attempts}`)
//     }
//     
//     if (auth0Instance.isLoading?.value) {
//       console.log('Auth0 still loading after timeout, proceeding anyway')
//     } else {
//       console.log('Auth0 finished loading')
//     }
//   }
//   
//   if (to.meta.requiresAuth) {
//     // Check if user is authenticated
//     if (!auth0Instance.isAuthenticated?.value) {
//       console.log('User not authenticated, redirecting to login')
//       console.log('Redirecting to Auth0 login...')
//       await auth0Instance.loginWithRedirect({
//         authorizationParams: {
//           redirect_uri: `${window.location.origin}/callback`
//         }
//       })
//       // Don't call next() here since loginWithRedirect will redirect the user
//       return
//     }
//     console.log('User authenticated, proceeding to route')
//     next()
//   } else {
//     // For non-protected routes, just proceed
//     next()
//   }
// })

// Mount app
console.log('Mounting Vue app...')
app.mount('#app')
console.log('Vue app mounted successfully') 
